<template>
  <section class="closefriend-hero h-100">
    <b @click="slideBar()" :class="showSlideBar ? 'screen-overlay show' : 'screen-overlay'"></b>


    <customer-menu @menuClicked="slideBar()" :show-slide-bar="showSlideBar"></customer-menu>
    <header-menu @slideMenu="slideBar()" :is-loading="isLoading"></header-menu>
    <div class="container-fluid mt-3">
      <div class="row justify-content-center">
        <div class="col-12 px-2 px-md-4" style="max-width: 1200px;">
          
          <div class="login-card mx-auto mb-4 p-3 p-md-4 mt-2">
            <global-message></global-message>

            <!-- Header with Add Card Button -->
            <div class="d-flex flex-wrap align-items-center justify-content-between mb-4 gap-2">
              <div>
                <h5 class="text-white font-weight-700 mb-0 font-size-20">Payment Methods</h5>
                <span class="text-muted font-size-12" v-if="!isLoading">{{ cards.length }} saved</span>
              </div>
              <button type="button" class="cp-btn-primary d-inline-flex align-items-center" @click="addCard()">
                <i class="fa fa-plus me-2 font-size-12"></i> Add Card
              </button>
            </div>
            
            <!-- Loading Skeleton State -->
            <div v-if="isLoading" class="row mt-2">
              <div class="col-12 col-md-6 col-lg-4 mb-4" v-for="i in 2" :key="i">
                <div class="credit-card-design p-4 d-flex flex-column justify-content-between" style="min-height: 210px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); animation: pulse 1.5s infinite;">
                  <div class="d-flex justify-content-between align-items-center">
                    <div style="width: 48px; height: 28px; background: rgba(255,255,255,0.08); border-radius: 6px;"></div>
                    <div style="width: 32px; height: 32px; background: rgba(255,255,255,0.08); border-radius: 50%;"></div>
                  </div>
                  <div class="my-3" style="height: 18px; width: 75%; background: rgba(255,255,255,0.08); border-radius: 4px;"></div>
                  <div class="d-flex justify-content-between align-items-center">
                    <div style="height: 14px; width: 30%; background: rgba(255,255,255,0.05); border-radius: 4px;"></div>
                    <div style="height: 14px; width: 25%; background: rgba(255,255,255,0.05); border-radius: 4px;"></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State (Only shown when not loading and 0 cards) -->
            <div v-else-if="cards.length === 0" class="text-center py-5">
              <div class="mb-4 d-flex justify-content-center">
                <div class="add-card-circle-icon cursor-pointer" style="width: 64px; height: 64px;" @click="addCard()">
                  <i class="fa fa-plus text-white font-size-24"></i>
                </div>
              </div>
              <h5 class="text-white font-weight-500 mb-2" style="opacity: 0.85;">No payment options</h5>
              <p class="text-muted font-size-14 mb-4" style="max-width: 380px; margin: 0 auto;">
                You haven't added any credit cards yet. Add a card for instant 1-click purchases and subscriptions.
              </p>
              <button type="button" class="cp-btn-primary d-inline-flex align-items-center" @click="addCard()">
                <i class="fa fa-plus me-2 font-size-12"></i> Add New Card
              </button>
            </div>

            <!-- Cards Grid with Circular Add Card Slot Below -->
            <div v-else class="row mt-2">
              <!-- Existing Cards -->
              <div class="col-12 col-md-6 col-lg-4 mb-4" v-for="(card, index) in cards" :key="index">
                <div class="credit-card-design position-relative" 
                     :class="{ 'is-selected': defaultCardIndex === index, 'cursor-pointer': defaultCardIndex !== index }"
                     @click="defaultCardIndex !== index && (defaultCardIndex = index, $refs.confirmSave.show())">
                  
                  <!-- Card Background & Shine -->
                  <div class="card-bg" :class="getBrandBgClass(card.brand)">
                    <div class="card-shine"></div>
                  </div>
                  
                  <!-- Card Content -->
                  <div class="card-content d-flex flex-column h-100 p-4" style="min-height: 210px;">
                    
                    <!-- Top Row: Icon & Actions -->
                    <div class="d-flex justify-content-between align-items-start">
                      <i class="fa fa-cc-visa font-size-36" v-if="card.brand.toLowerCase() === 'visa'" style="opacity: 0.9;"></i>
                      <i class="fa fa-cc-mastercard font-size-36" v-else-if="card.brand.toLowerCase() === 'mastercard'" style="opacity: 0.9;"></i>
                      <i class="fa fa-cc-amex font-size-36" v-else-if="card.brand.toLowerCase() === 'amex'" style="opacity: 0.9;"></i>
                      <i class="fa fa-credit-card font-size-32" v-else style="opacity: 0.8;"></i>
                      
                      <div class="d-flex align-items-center" style="gap: 10px; z-index: 5;">
                        <div class="card-action-btn edit-btn" @click.stop="cardIndex = index; editCard()">
                          <i class="fa fa-pencil" style="font-size: 18px;"></i>
                        </div>
                        <div class="card-action-btn delete-btn" @click.stop="cardIndex = index; $refs.confirmDelete.show()">
                          <i class="fa fa-trash" style="font-size: 18px;"></i>
                        </div>
                      </div>
                    </div>

                    <!-- Middle: Card Number -->
                    <div class="mt-auto mb-4 pt-4">
                      <div class="font-weight-bold text-white card-number" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                        **** **** **** {{ card.last4Digit }}
                      </div>
                    </div>

                    <!-- Bottom Row: Expiry & Brand Text -->
                    <div class="d-flex justify-content-between align-items-end mt-auto">
                      <div class="d-flex flex-column">
                        <span class="text-uppercase" style="font-size: 11px; opacity: 0.8; letter-spacing: 1px;">Expires</span>
                        <span class="font-weight-bold" style="font-size: 15px; letter-spacing: 2px;">{{ card.expMonth }}/{{ card.expYear }}</span>
                      </div>
                      
                      <div class="font-weight-bold text-uppercase" style="font-size: 18px; font-style: italic; letter-spacing: 1px; opacity: 0.9; text-shadow: 0 2px 4px rgba(0,0,0,0.3);">
                        {{ card.brand }}
                      </div>
                    </div>
                  </div>
                  
                  <!-- Selected State Indicator -->
                  <div class="selected-badge" v-if="defaultCardIndex === index">
                    <i class="fa fa-check"></i>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

        <model-confirm-save
          id="save_model"
          ref="confirmSave"
          text="Would you like to make this card your default payment method?"
          confirm-btn-txt="Save"
          icon-class="fa-check"
          @cancel="setDefaultCardIndex()"
          @confirm="markAsDefaultCard()"/>

        <model-confirm-delete
          id="delete_model"
          ref="confirmDelete"
          text="Would you like to delete this card?"
          button-color="btn-danger border-0"
          confirm-btn-txt="Delete"
          @confirm="deleteCard()"
        />

        <model-edit-card
          id="edit_card_modal"
          ref="editCardModal"
          :card="cardToEdit"
          :is-loading="isLoading"
          @save="saveEditedCard"
        />

        <model-add-card
          id="add_card_modal"
          ref="addCardModal"
          :is-loading="isLoading"
          @save="saveNewCard"
        />
  </section>
</template>

<script>

import HeaderMenu from "@/components/module/influencer/shared/HeaderMenu";
import CustomerMenu from "@/components/module/customer/shared/CustomerMenu";
import GlobalMessage from "@/components/module/shared/GlobalMessage";
import {SERVICE_PAYMENT} from "@/properties/micro-service";
import ModelConfirmSave from "@/components/module/customer/shared/ModelBasic";
import ModelConfirmDelete from "@/components/module/customer/shared/ModelBasic";
import ModelEditCard from "@/components/module/customer/shared/ModelEditCard";
import ModelAddCard from "@/components/module/customer/shared/ModelAddCard";
import CustomerPwaActionBar from "@/components/module/customer/shared/CustomerPwaActionBar";
import {igValidationMixin} from "@/mixin/ig-validation-mixin";
import {mapActions} from "vuex";
import titleMixin from "../../../../mixin/title-mixin";

const URL = `${SERVICE_PAYMENT}/api/v1/stripe/customers`;
export default {
  name: 'PaymentOption',
  title: 'Payment options',
  mixins: [igValidationMixin, titleMixin],
  components: {
    HeaderMenu,
    CustomerMenu,
    CustomerPwaActionBar,
    GlobalMessage,
    ModelConfirmSave,
    ModelConfirmDelete,
    ModelEditCard,
    ModelAddCard
  },
  data() {
    return {
      user: {},
      cards: [],
      defaultCardIndex: null,
      cardIndex: null,
      cardToEdit: {},

      showSlideBar: false
    }
  },
  created() {
    this.user = this.getUser();
    let url = `${URL}/${this.user.id}/cards`;
    this.getData(url, (data) => {
      this.cards = data || [];
      this.setDefaultCardIndex();
      this.initIGValidity();
      if (this.$route.query.addCard) {
        this.addCard();
      }
    });
  },
  methods: {
    ...mapActions['setMessage'],
    setDefaultCardIndex() {
      this.defaultCardIndex = this.cards.findIndex((card) => card.defaultCard === true);
    },
    getCardDetails(card) {
      return `${card.brand} .... ${card.last4Digit}`
    },
    getCardExpiry(card) {
      return `${card.expMonth}/${card.expYear}`;
    },
    getBrandBgClass(brand) {
      if (!brand) return 'bg-default-card';
      const b = brand.toLowerCase();
      if (b === 'visa') return 'bg-visa';
      if (b === 'mastercard') return 'bg-mastercard';
      if (b === 'amex') return 'bg-amex';
      if (b === 'discover') return 'bg-discover';
      return 'bg-default-card';
    },
    editCard() {
      this.cardToEdit = this.cards[this.cardIndex];
      this.$nextTick(() => {
        this.$refs.editCardModal.show();
      });
    },
    saveEditedCard(updatedCard) {
      let url = `${URL}/${this.user.id}/cards/${updatedCard.cardId}/`;
      this.patchData(url, updatedCard, (data) => {
        this.cards.splice(this.cardIndex, 1, updatedCard);
        this.$refs.editCardModal.hide();
        this.setMessage('SAVED.SUCCESSFULLY');
      });
    },
    deleteCard() {
      this.$refs.confirmDelete.hide();
      let url = `${URL}/${this.user.id}/cards/${this.cards[this.cardIndex].cardId}/`;
      this.getDelete(url, (data) => {
        this.cards.splice(this.cardIndex, 1);
        this.setMessage('DELETED.SUCCESSFULLY');
      });
    },
    markAsDefaultCard() {
      this.$refs.confirmSave.hide();
      for (let i = 0; i < this.cards.length; i++) {
        this.cards[i].defaultCard = false;
      }
      this.cards[this.defaultCardIndex].defaultCard = true;

      let card = this.cards[this.defaultCardIndex];
      let url = `${URL}/${this.user.id}/cards/${card.cardId}/`;
      this.patchData(url, card, (data) => {
        this.setDefaultCardIndex();
        this.setMessage('SAVED.SUCCESSFULLY');
      });
    },
    addCard() {
      this.$nextTick(() => {
        if (this.$refs.addCardModal) {
          this.$refs.addCardModal.show();
        }
      });
    },
    saveNewCard(newCard) {
      let url = `${URL}/${this.user.id}/cards`;
      this.postData(url, newCard, (data) => {
        // API returns the created card data
        this.cards.push(data);
        if (this.$refs.addCardModal) {
          this.$refs.addCardModal.hide();
        }
        this.setMessage('SAVED.SUCCESSFULLY');
        
        // If it's the first card, make it default
        if (this.cards.length === 1) {
          this.defaultCardIndex = 0;
          let defaultUrl = `${URL}/${this.user.id}/cards/${data.cardId}/`;
          data.defaultCard = true;
          this.patchData(defaultUrl, data, () => {
             this.setDefaultCardIndex();
             this.handlePostSaveRedirect(data.cardId);
          });
        } else {
          this.handlePostSaveRedirect(data.cardId);
        }
      });
    },
    handlePostSaveRedirect(cardId) {
      if (this.$route.query.redirect) {
        let redirectUrl = this.$route.query.redirect;
        const separator = redirectUrl.includes('?') ? '&' : '?';
        this.$router.replace(`${redirectUrl}${separator}cardAdded=${cardId}`);
      }
    },
    slideBar() {
      this.showSlideBar = !this.showSlideBar;
    }
  }
}

</script>

<style scoped>
/* Credit Card Design */
.credit-card-design {
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  border-radius: 20px;
  position: relative;
  overflow: visible;
  box-shadow: 0 10px 30px rgba(0,0,0,0.6);
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  border: 1px solid rgba(255,255,255,0.1);
  background: #1a1a1a;
}

.credit-card-design:not(.is-selected):hover {
  transform: translateY(-5px) scale(1.02);
  box-shadow: 0 15px 35px rgba(229, 59, 185, 0.15);
}

.is-selected {
  border: 2px solid #F3DA6B;
  box-shadow: 0 10px 40px rgba(243, 218, 107, 0.25);
}

.card-bg {
  position: absolute;
  inset: 0;
  border-radius: 20px;
  overflow: hidden;
  z-index: 1;
}

/* Colorful Brands */
.bg-visa { background: linear-gradient(135deg, #1A1F71 0%, #004B96 100%); }
.bg-mastercard { background: linear-gradient(135deg, #EB001B 0%, #FF5F00 100%); }
.bg-amex { background: linear-gradient(135deg, #002663 0%, #4D90C7 100%); }
.bg-discover { background: linear-gradient(135deg, #E55C20 0%, #F58220 100%); }
.bg-default-card { background: linear-gradient(135deg, #2b2b2b 0%, #111111 100%); }

.card-bg::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.15) 0%, transparent 40%),
              radial-gradient(circle at 100% 0%, rgba(255, 255, 255, 0.05) 0%, transparent 40%);
  z-index: 2;
  mix-blend-mode: overlay;
}

.card-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0) 100%);
  transform: skewX(-25deg);
  z-index: 3;
  transition: 0.6s ease-in-out;
}

.credit-card-design:not(.is-selected):hover .card-shine {
  left: 200%;
}

.card-content {
  position: relative;
  z-index: 10;
  color: white;
}

.card-number {
  font-size: 22px;
  letter-spacing: 4px;
  font-family: 'Courier New', Courier, monospace;
  text-shadow: 0 2px 4px rgba(0,0,0,0.5);
}

@media (max-width: 576px) {
  .card-number {
    font-size: 18px;
    letter-spacing: 2px;
  }
}

.card-action-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0,0,0,0.4);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.7);
  transition: all 0.2s ease;
  border: 1px solid rgba(255,255,255,0.1);
  cursor: pointer;
}

.card-action-btn:hover {
  background: rgba(255,255,255,0.2);
  color: white;
  transform: scale(1.1);
}
.delete-btn:hover {
  background: rgba(255, 75, 129, 0.3);
  color: #FF4B81;
  border-color: rgba(255, 75, 129, 0.5);
}

.selected-badge {
  position: absolute;
  top: -12px;
  right: -12px;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #F3DA6B, #E53BB9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  box-shadow: 0 4px 10px rgba(0,0,0,0.3);
  color: white;
  font-size: 14px;
  border: 2px solid #0a0a0a;
}

/* Circular Add Card Slot Below */
.credit-card-add-slot {
  min-height: 210px;
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  border-radius: 20px;
  border: 2px dashed rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.credit-card-add-slot:hover {
  border-color: #E53BB9;
  background: rgba(229, 59, 185, 0.05);
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(229, 59, 185, 0.15);
}

.add-card-circle-icon {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: linear-gradient(135deg, #F3DA6B 0%, #E53BB9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 20px rgba(229, 59, 185, 0.4);
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.credit-card-add-slot:hover .add-card-circle-icon,
.add-card-circle-icon:hover {
  transform: scale(1.12) rotate(90deg);
}

</style>
